import React from 'react';
import {
  Tabs,
  Button,
  Input,
  Row,
  Col,
  Timeline,
  Empty,
  message,
} from 'antd';
import {
  CommentOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import withStyles from 'react-jss';
import { addCommentForInvoice, fetchEventHistory, fetchCommentHistory } from './Receivables.api';
import { ReceivablesRequestActionStyles } from './Receivables.actionDrawerStyles';
import { StylesProps } from 'src/library/types';
import { formatDateTime, formatDateTimeFromNow, transformName } from 'src/utils/utils';

const { TabPane } = Tabs;

interface IProps extends StylesProps<ReturnType<typeof ReceivablesRequestActionStyles>> {
  data: any;
  isReadonly?: boolean;
}

const CommentsAndEventSection = (props: IProps): React.ReactElement => {
  const {
    classes,
    data,
    isReadonly,
    ...rest
  } = props;
  const defaultActiveKey = 'comments';
  const [activeKey, setActiveKey] = React.useState(defaultActiveKey);
  const [comments, setComments] = React.useState('');
  const [eventsTrackingHistory, setEventsTrackingHistory] = React.useState([]);
  const [commentsTrackingHistory, setCommentsTrackingHistory] = React.useState([]);

  React.useEffect(() => {
    setActiveKey(defaultActiveKey);
  }, [defaultActiveKey]);

  const fetchHistoryData = React.useCallback(async () => {
    try {
      if (!data?.id) {
        return;
      }

      const [eventsResponse, commentsResponse] = await Promise.all([
        fetchEventHistory({ id: String(data?.id) }),
        fetchCommentHistory({ id: String(data?.id) }),
      ]);

      if (eventsResponse.isSuccess) {
        const eventsData = eventsResponse.data || [];
        const sortedEvents = [...eventsData].sort((a, b) =>
          new Date(b.event_time).getTime() - new Date(a.event_time).getTime());
        setEventsTrackingHistory(sortedEvents);
      } else {
        message.error('Failed to fetch events');
      }

      if (commentsResponse.isSuccess) {
        setCommentsTrackingHistory(commentsResponse.data || []);
      } else {
        message.error('Failed to fetch comments');
      }
    } catch (err) {
      message.error('Error fetching history data');
    }
  }, [data?.id]);

  React.useEffect(() => {
    setEventsTrackingHistory([]);
    setCommentsTrackingHistory([]);
    fetchHistoryData();
  }, [fetchHistoryData]);

  const handleReload = () => {
    fetchHistoryData();
  };

  const onChange = (key: string) => {
    setActiveKey(key);
  };

  const renderPrettyDateTime = (text) => {
    return text ? (
      <div style={{ marginRight: '5px', textAlign: 'right' }}>
        <div style={{ fontWeight: 300, fontSize: '14px' }}>
          {formatDateTime(text, 'DD MMM, YYYY', '-')}
        </div>
        <div
          style={{ fontSize: '12px', marginRight: '5px' }}
        >
          <span style={{ color: '#595959', fontWeight: 100 }}>
            {formatDateTime(text, 'hh:mm A', '-')}
          </span>
        </div>
      </div>
    ) : null;
  };

  const handleAddComment = async () => {
    try {
      const body = {
        id: String(data?.id),
        comment: comments,
      };
      const response = await addCommentForInvoice(body);
      if (response.isSuccess) {
        message.success('Comment Added successfully');
      } else {
        message.error(response.errorMessage);
      }
    } catch (err) {
      message.error(err.message);
      return;
    }
    setComments('');
    handleReload();
  };

  const renderAddCommentAndAttachment = () => {
    return (
      <div className={classes.commentBorder}>
        <Input.TextArea
          placeholder="Type Here..."
          autoSize={{ minRows: 3, maxRows: 5 }}
          value={comments}
          onChange={(e) => setComments(e.target.value)}
        />
        <Row justify="end" style={{ marginTop: '6px' }}>
          <Button
            type="primary"
            onClick={() => handleAddComment()}
          >
            Comment
          </Button>
        </Row>
      </div>
    );
  };

  const renderEventsTab = () => (
    <div style={{ marginTop: '18px' }}>
      <div className={classes.rectangleTrackInfo} style={{ marginTop: '18px' }}>
        {eventsTrackingHistory?.length ?
          <div style={{ margin: 24, maxHeight: '500px', overflowY: 'auto' }}>
            <Timeline>
              {
                eventsTrackingHistory.map((x, i) => {
                  return (
                    <Row align="top" key={x.id || x.event_time || i}>
                      <Col span={6} style={{ marginRight: '10px' }}>
                        <span
                          className={classes.timeStamp}
                        >
                          {renderPrettyDateTime(x.event_time)}
                        </span>
                      </Col>

                      <Col span={2}>
                        <Timeline.Item
                          className={i < eventsTrackingHistory.length - 1 ?
                            classes.timelineTail : classes.timelineTailHide}
                          dot={<div style={{ height: '10px', width: '10px', borderRadius: '50%', background: '#0C7AF9' }} />}
                        />
                      </Col>
                      <Col span={14}>
                        <div style={{ marginLeft: '10px' }}>
                          <div className={classes.trackStatus}>
                            {transformName(x.event_type)}
                          </div>
                          <div style={{ fontSize: '12px' }}>
                            {transformName(x.creator_name)}
                          </div>
                        </div>
                      </Col>
                    </Row>
                  );
                })
              }
            </Timeline>
          </div> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
      </div>
    </div>
  );

  const renderChatHistory = () => (
    <div className={classes.commentsTrackingContainer}>
      <div className={classes.commentsContainer}>
        {
          commentsTrackingHistory?.map((log, idx) => (
            <div
              className={classes.commentsDetailsDataContainer}
              key={log.id || log.created_at || idx}
            >
              <div className={classes.commentsDetailsDataHeaderContainer}>
                {transformName(log.creator_name || 'User')} &nbsp; &bull; &nbsp;
                <p style={{ color: 'grey' }}> {formatDateTimeFromNow(log.created_at)} </p>
              </div>
              <div className={classes.commentsDetailsDataCommentContainer}>
                {log.comment}
              </div>
            </div>
          ))
        }
      </div>
    </div>
  );

  return (
    <>
      <style>
        {`
        .ant-tabs-nav-more {
          display: none !important;
        }
      `}
      </style>
      <Tabs
        defaultActiveKey='comments'
        type="card"
        {...rest}
        onChange={onChange}
        className={classes.commentsTabContainer}
      >
        <TabPane
          tab={
            <span className={classes.commentsTabpane}>
              <CommentOutlined />
              Comments
            </span>
          }
          key='comments'
        >
          {renderChatHistory()}
        </TabPane>
        <TabPane
          tab={
            <span className={classes.commentsTabpane}>
              <HistoryOutlined />
              Events
            </span>
          }
          key='events'
        >
          {renderEventsTab()}
        </TabPane>
      </Tabs>
      {activeKey === 'comments' && !isReadonly && (
        <div>
          {renderAddCommentAndAttachment()}
        </div>
      )}
    </>
  );
};

export default withStyles(ReceivablesRequestActionStyles)(CommentsAndEventSection);
